import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { format, isAfter, isBefore, parse } from 'date-fns';
import { useNavigate } from 'react-router-dom';

import { ModalSize } from 'components/modal';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { serializeServiceMode } from 'state/mParticle/utils';
import { ServiceMode, useOrderContext } from 'state/order';
import { useStoreContext } from 'state/store';
import { fifteenMins, minsInHour, msInSecond, timeSelectorTo24Hour } from 'utils/dateTime';
import { routes } from 'utils/routing';

import DeliveryDetails from './delivery-details';
import { getDayPlusOffsetHoursOnOpenHours, getNinetyDays } from './get-day-plus-offset-hours';
import offsetHours from './offset-hours';
import PickupDetails from './pickup-details';
import ServiceModeCatering from './service-mode-catering';
import { StyledModal } from './styled';
import { useDisplayText } from './use-display-text';
import useTimeOptions, {
  END_CATERING_PICKUP_HOUR,
  START_CATERING_PICKUP_HOUR,
} from './use-time-options';

const formatDate = (date: Date) => format(date, 'yyyy-MM-dd');
const msInFifteenMins = fifteenMins * minsInHour * msInSecond;
const getFirstAvailableTime = (date: Date) =>
  format(new Date(msInFifteenMins * Math.ceil(date.getTime() / msInFifteenMins)), 'h:mm aaaa');

const ServiceModeModal: FC = () => {
  const displayText = useDisplayText();
  const {
    emptyCart,
    setOrderCateringPickupDateTime,
    selectServiceMode: orderContextSelectServiceMode,
  } = useOrderContext();
  const { resetStore } = useStoreContext();
  const { logEvent } = useMParticleContext();
  const enableCateringDelivery = useFlag(LaunchDarklyFlag.ENABLE_CATERING_DELIVERY);
  const enableCatering = useFlag(LaunchDarklyFlag.ENABLE_CATERING);
  const cateringDirectToPickup =
    window.location.pathname === routes.serviceModeCateringPickup
      ? ServiceMode.CATERING_PICKUP
      : null;
  const cateringDirectToDelivery =
    window.location.pathname === routes.serviceModeCateringDelivery
      ? ServiceMode.CATERING_DELIVERY
      : null;
  const [mode, setMode] = useState<ServiceMode | null>(
    cateringDirectToPickup || cateringDirectToDelivery
  );
  // setup initial date and time offset in advance
  const now = useMemo(() => new Date(), []);
  const nowPlusOffsetHours = useMemo(
    () =>
      getDayPlusOffsetHoursOnOpenHours(
        now,
        offsetHours,
        START_CATERING_PICKUP_HOUR,
        END_CATERING_PICKUP_HOUR
      ),
    [now]
  );

  const ninetyDays = useMemo(() => getNinetyDays(now), [now]);

  const firstAvailableTime = useMemo(() => getFirstAvailableTime(nowPlusOffsetHours), [
    nowPlusOffsetHours,
  ]);

  const [date, setDate] = useState<string>(formatDate(nowPlusOffsetHours));
  const [time, setTime] = useState<string>(firstAvailableTime);
  const [validDateTime, setValidDateTime] = useState<boolean>(true);
  const navigate = useNavigate();

  // Verify that date and time fall within valid range
  useEffect(() => {
    const selectedDateTimeDate = parse(`${date} ${time}`, 'yyyy-MM-dd h:mm aaaa', now);
    const isValid = !!(
      date &&
      time &&
      isAfter(selectedDateTimeDate, nowPlusOffsetHours) &&
      isBefore(selectedDateTimeDate, ninetyDays)
    );
    setValidDateTime(isValid);
  }, [date, ninetyDays, now, nowPlusOffsetHours, time]);

  const logCancelCateringOrder = useCallback(() => {
    logEvent(CustomEventNames.CANCEL_CATERING_ORDER, EventTypes.Other);
  }, [logEvent]);

  const logSelectCateringServiceMode = (selectedMode: ServiceMode | null) => {
    logEvent(CustomEventNames.SELECT_CATERING_SERVICE_MODE, EventTypes.Other, {
      'Service Mode': serializeServiceMode(selectedMode),
    });
  };

  const cateringDateTimeEvent = (targetDate: string, targetTime: string) => {
    logEvent(CustomEventNames.CATERING_DATE_TIME_SELECT, EventTypes.Other, {
      'Catering Date': targetDate,
      'Catering Time': targetTime,
    });
  };

  const selectServiceMode = (selectedMode: ServiceMode | null) => {
    setMode(selectedMode);
    logSelectCateringServiceMode(selectedMode);

    switch (selectedMode) {
      case ServiceMode.CATERING_PICKUP:
        navigate(routes.serviceModeCateringPickup);
        break;
      case ServiceMode.CATERING_DELIVERY:
        navigate(routes.serviceModeCateringDelivery);
        break;
      default:
        break;
    }
  };

  const onBack = () => {
    setMode(null);
    orderContextSelectServiceMode(null);
    navigate(-1);
  };

  const onContinue = () => {
    setOrderCateringPickupDateTime(`${date} ${time}`);
    cateringDateTimeEvent(date, timeSelectorTo24Hour(time));
    logSelectCateringServiceMode(mode);
    orderContextSelectServiceMode(mode).then((updatedSvcMode: boolean) => {
      if (!updatedSvcMode) {
        return;
      }
      resetStore();

      return navigate(routes.storeLocator);
    });
  };

  const timeOptions = useTimeOptions({
    date,
    firstAvailableTime,
    now,
    nowPlusOffsetHours,
  });

  const resetServiceModeAndStore = () => {
    orderContextSelectServiceMode(null);
    resetStore();
    emptyCart();
    navigate(routes.storeLocator);
  };

  const handleDismiss = () => {
    resetServiceModeAndStore();
  };

  return (
    <StyledModal
      size={ModalSize.SMALL}
      onDismiss={handleDismiss}
      mParticleEventData={{
        modalAppearanceEventMessage: 'Catering Service Mode Details',
      }}
      height={Styles.layout.smallerSectionMaxWidth}
    >
      {!mode && (
        <ServiceModeCatering
          enableDelivery={enableCateringDelivery}
          enablePickup={enableCatering}
          selectServiceMode={selectServiceMode}
          handleCancel={resetServiceModeAndStore}
          handleCancelCatering={logCancelCateringOrder}
        />
      )}
      {mode === ServiceMode.CATERING_DELIVERY && <DeliveryDetails />}
      {mode === ServiceMode.CATERING_PICKUP && (
        <PickupDetails
          date={date}
          minDate={formatDate(nowPlusOffsetHours)}
          maxDate={formatDate(ninetyDays)}
          time={time}
          timeOptions={timeOptions}
          validDateTime={validDateTime}
          onDateChange={({ target: { value } }) => setDate(value)}
          onTimeChange={({ target: { value } }) => setTime(value)}
          onBack={onBack}
          onContinue={onContinue}
          headingText={displayText.pickupDetailsHeading}
          subheadingText={displayText.pickupDetailsSubheading}
          backButtonText={displayText.backButton}
          continueButtonText={displayText.continueButton}
        />
      )}
    </StyledModal>
  );
};

export default ServiceModeModal;
